import { CheckCircle2, Circle, ArrowRight, X } from "lucide-react";
import { useState } from "react";

/*
 * Onboarding card — shown to new signups until they've completed the 4 core setup
 * steps. Dismissable (persisted in localStorage per user). Never blocks the
 * dashboard; it lives at the top like a friendly checklist.
 *
 * Steps (all inferred from live data — nothing to persist server-side):
 *   1. Start free trial      → subscription.status === "active"
 *   2. Add your website URL  → org.website not empty
 *   3. Save your donation URL (optional but nudged) → org.donation_url OR auto-detected
 *   4. Run your first Mission Watch Score → report.has_run === true
 */

export default function OnboardingChecklist({
  user, subscription, org, report,
  onStartTrial, onRunCheckup, onFocusWebsite,
}) {
  const storageKey = `mw:onboarding-dismissed:${user?.id || user?.email || "anon"}`;
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(storageKey) === "1";
  });

  const trialStarted = subscription?.status === "active";
  const websiteAdded = Boolean(org?.website);
  const donationConfigured = Boolean(org?.donation_url) || websiteAdded; // auto-detect counts as done
  const firstScan = Boolean(report?.has_run);

  const steps = [
    { done: trialStarted, label: "Start your free 7-day trial", hint: "Card required · no charge for 7 days",
      cta: !trialStarted ? { label: "Start free trial", onClick: onStartTrial } : null,
      testid: "onboarding-step-trial" },
    { done: websiteAdded, label: "Add your website URL", hint: "So we know what to watch",
      cta: !websiteAdded ? { label: "Add website", onClick: onFocusWebsite } : null,
      testid: "onboarding-step-website" },
    { done: donationConfigured, label: "Save your donation link", hint: "Optional — we'll auto-detect from your site if left blank",
      cta: !donationConfigured ? { label: "Add donation URL", onClick: onFocusWebsite } : null,
      testid: "onboarding-step-donation" },
    { done: firstScan, label: "Run your first Mission Watch Score", hint: "Takes about 60 seconds",
      cta: !firstScan && trialStarted && websiteAdded
        ? { label: "Run first check", onClick: onRunCheckup }
        : null,
      testid: "onboarding-step-scan" },
  ];

  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;

  // Hide entirely once all done OR the user explicitly dismisses it.
  if (dismissed || allDone) return null;

  const dismiss = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, "1");
    }
    setDismissed(true);
  };

  return (
    <section
      className="mb-6 rounded-2xl bg-gradient-to-br from-charcoal-900 to-charcoal-800 text-white p-6 sm:p-7 relative overflow-hidden"
      data-testid="onboarding-card"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand/20 blur-3xl pointer-events-none" />
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-white/10 transition-colors grid place-items-center text-white/60 hover:text-white"
        aria-label="Dismiss onboarding checklist"
        data-testid="onboarding-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="relative">
        <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Get started</p>
        <h3 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight leading-tight">
          Set up in 4 quick steps — {completed} of {steps.length} done
        </h3>
        <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-emerald-400 transition-all"
            style={{ width: `${(completed / steps.length) * 100}%` }}
          />
        </div>
        <ul className="mt-5 space-y-2">
          {steps.map((s) => (
            <li key={s.label} className="flex items-center gap-3 py-2.5 px-3.5 rounded-xl bg-white/5 border border-white/10" data-testid={s.testid}>
              {s.done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={2} />
                : <Circle className="w-4 h-4 text-white/40 shrink-0" strokeWidth={2} />}
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${s.done ? "text-white/50 line-through" : "text-white/90"}`}>{s.label}</p>
                <p className="text-[11px] font-mono text-white/40 mt-0.5">{s.hint}</p>
              </div>
              {s.cta && (
                <button
                  type="button"
                  onClick={s.cta.onClick}
                  className="inline-flex items-center gap-1 text-xs font-medium text-white bg-brand hover:bg-brand-hover px-3 py-1.5 rounded-full transition-colors shrink-0"
                  data-testid={`${s.testid}-cta`}
                >
                  {s.cta.label} <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
        {/* Iteration 35: teach new users the three service layers.
            Sits directly under the setup checklist so they see WHAT
            Mission Watch is going to do for them as soon as setup
            finishes. Warm, non-technical language.  */}
        <div
          className="mt-6 rounded-xl bg-white/[0.03] border border-white/10 p-4 sm:p-5"
          data-testid="onboarding-three-layer-teach"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
            What happens next
          </p>
          <ol className="mt-3 space-y-2.5 text-sm text-white/85">
            <li className="flex gap-3">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center text-[10px] font-mono text-emerald-300 shrink-0">1</span>
              <span>
                First, <span className="text-emerald-300 font-medium">Mission Watch Protection</span>{" "}
                begins protecting your critical pages.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center text-[10px] font-mono text-emerald-300 shrink-0">2</span>
              <span>
                Each day, <span className="text-emerald-300 font-medium">Mission Watch Audit</span>{" "}
                inspects your website end to end.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center text-[10px] font-mono text-emerald-300 shrink-0">3</span>
              <span>
                Each week, your updated{" "}
                <span className="text-emerald-300 font-medium">Website Health Report</span>{" "}
                lands in your inbox.
              </span>
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
}
